/**
 * V2 Follows — what you follow, and the queries that depend on it.
 *
 * CATEGORIES are Yoink's primary follow object (user_category_follows,
 * shared with V1 — a user's existing follows carry straight over).
 * Following a user (user_follows) is the additive extra. Both feed the
 * single "Following" feed.
 *
 * Store slices owned here: followedCategoryIds, followingIds. Loaded
 * once per login (main.js triggers on user change); follow/unfollow
 * update them after the write succeeds.
 *
 * The Following feed goes through the get_followed_feed RPC
 * (supabase/migrations/20260712b_phase3_followed_feed.sql): followed-category subtrees and
 * followed users join server-side per the scale rules — the client
 * never ships an in.(...) list of ids.
 */
import * as gateway from '../../core/gateway.js';
import { cached, invalidate } from '../../core/query.js';
import { drainRows } from '../../core/drain.js';
import { FEED_SELECT, FEED_PAGE_SIZE } from '../../ui/card.js';
import { attachPersonalShelves } from '../journal/journal.js';
import { markComplete } from '../community/tutorial.js';

/** Load the signed-in user's followed categories and users into the store. */
export async function loadFollowing(store) {
  const user = store.get().user;
  if (!user) return;
  // Drained past the old silent 1000-row cap (assessment 1.5). The
  // followed id itself cursors the pages — it's unique per user.
  const [cats, users] = await Promise.all([
    drainRows(gateway.TABLES.USER_CATEGORY_FOLLOWS, {
      select: 'category_id',
      filters: { user_id: `eq.${user.id}` },
      uniqueKey: 'category_id',
    }).catch(() => null),
    drainRows(gateway.TABLES.USER_FOLLOWS, {
      select: 'followee_id',
      filters: { follower_id: `eq.${user.id}` },
      uniqueKey: 'followee_id',
    }).catch(() => null),
  ]);
  store.set({
    followedCategoryIds: cats === null ? [] : cats.map((row) => row.category_id),
    followingIds: users === null ? [] : users.map((row) => row.followee_id),
  });
}

export function isFollowing(store, userId) {
  return (store.get().followingIds || []).includes(userId);
}

export async function follow(store, followeeId) {
  const me = store.get().user;
  if (!me || me.id === followeeId) return;
  const { error } = await gateway.rawInsert(gateway.TABLES.USER_FOLLOWS, {
    body: { follower_id: me.id, followee_id: followeeId },
  });
  // 409 = already following (double-tap, second device) — converged, not an error
  if (error && !/HTTP 409/.test(error.message)) throw error;
  // Invalidate BEFORE the store write: the followingIds change triggers
  // the unread-badge refresh synchronously (initUnread's subscriber),
  // which must not hit a stale cached page
  invalidate('unread-follows:');
  store.set({ followingIds: [...new Set([...(store.get().followingIds || []), followeeId])] });
  invalidate('followed-feed:');
}

export async function unfollow(store, followeeId) {
  const me = store.get().user;
  if (!me) return;
  const { error } = await gateway.rawDelete(gateway.TABLES.USER_FOLLOWS, {
    filters: { follower_id: `eq.${me.id}`, followee_id: `eq.${followeeId}` },
  });
  if (error) throw error;
  invalidate('unread-follows:'); // before the store write — see follow()
  store.set({
    followingIds: (store.get().followingIds || []).filter((id) => id !== followeeId),
  });
  invalidate('followed-feed:');
}

/**
 * One page of the Following feed (followed-category subtrees ∪ followed
 * users), keyset-paginated like Discover. Resource embedding works on
 * the RPC because it returns setof yoinks.
 */
export async function fetchFollowedFeedPage({ cursor = null, categoryId = null, search = '' } = {}) {
  const key = `followed-feed:${categoryId || '-'}:${search || '-'}:${cursor ? `${cursor.created_at}/${cursor.id}` : 'first'}`;
  return cached(key, async () => {
    const base = {
      p_cursor_created_at: cursor?.created_at ?? null,
      p_cursor_id: cursor?.id ?? null,
      p_limit: FEED_PAGE_SIZE,
    };
    // Filter params exist only after supabase/migrations/20260712c_followed_feed_filters.sql —
    // send them when used, and fall back to the unfiltered feed if the
    // deployed function predates them (PostgREST 404s unknown args)
    const withFilters = categoryId || search;
    const params = withFilters
      ? { ...base, p_category_filter: categoryId || null, p_search: search || null }
      : base;
    let { data, error } = await gateway.rawRpc('get_followed_feed', {
      params, select: FEED_SELECT,
    });
    if (error && withFilters) {
      ({ data, error } = await gateway.rawRpc('get_followed_feed', {
        params: base, select: FEED_SELECT,
      }));
    }
    if (error) throw error;
    return attachPersonalShelves(data || []); // followed personal yoinks name their shelf
  });
}

// One bounded-map-sized load of the Following feed (matches Discover's
// MAP_LIMIT). The RPC has no bounds parameter, so the caller filters to
// the viewport client-side (rowsInArea) — at followed-feed scale that's
// fine, and the cache key stays viewport-free so panning never re-fetches.
const MAP_ROWS = 1000;

/**
 * The Following feed for map mode: newest MAP_ROWS rows, category/search
 * filtered server-side like fetchFollowedFeedPage (with the same
 * pre-filters-SQL fallback). Cache key shares the followed-feed: prefix
 * so follow/unfollow invalidation covers it.
 */
export async function fetchFollowedMapPage({ categoryId = null, search = '' } = {}) {
  const key = `followed-feed:map:${categoryId || '-'}:${search || '-'}`;
  return cached(key, async () => {
    const base = {
      p_cursor_created_at: null,
      p_cursor_id: null,
      p_limit: MAP_ROWS,
    };
    const withFilters = categoryId || search;
    const params = withFilters
      ? { ...base, p_category_filter: categoryId || null, p_search: search || null }
      : base;
    let { data, error } = await gateway.rawRpc('get_followed_feed', {
      params, select: FEED_SELECT,
    });
    if (error && withFilters) {
      ({ data, error } = await gateway.rawRpc('get_followed_feed', {
        params: base, select: FEED_SELECT,
      }));
    }
    if (error) throw error;
    return attachPersonalShelves(data || []); // followed personal yoinks name their shelf
  });
}

// ---------------------------------------------------------------------------
// Category follows — the primary follow object (same table as V1)
// ---------------------------------------------------------------------------

export function isFollowingCategory(store, categoryId) {
  return (store.get().followedCategoryIds || []).includes(categoryId);
}

export async function followCategory(store, categoryId) {
  const me = store.get().user;
  if (!me) return;
  const { error } = await gateway.rawInsert(gateway.TABLES.USER_CATEGORY_FOLLOWS, {
    body: { user_id: me.id, category_id: categoryId },
  });
  if (error && !/HTTP 409/.test(error.message)) throw error;
  store.set({
    followedCategoryIds: [...new Set([...(store.get().followedCategoryIds || []), categoryId])],
  });
  markComplete('follow_category');
  invalidate('followed-feed:');
}

export async function unfollowCategory(store, categoryId) {
  const me = store.get().user;
  if (!me) return;
  const { error } = await gateway.rawDelete(gateway.TABLES.USER_CATEGORY_FOLLOWS, {
    filters: { user_id: `eq.${me.id}`, category_id: `eq.${categoryId}` },
  });
  if (error) throw error;
  store.set({
    followedCategoryIds: (store.get().followedCategoryIds || []).filter((id) => id !== categoryId),
  });
  invalidate('followed-feed:');
}
